import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Package, AlertTriangle, Edit, Trash2, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';

function Estoque() {
  const [pecas, setPecas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPeca, setEditingPeca] = useState(null);
  const [margem, setMargem] = useState('');
  const [formData, setFormData] = useState({
    tipo: 'peca',
    nome: '',
    codigo: '',
    categoria_id: '',
    fornecedor_id: '',
    quantidade: '',
    estoque_minimo: '',
    preco_custo: '',
    preco_venda: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pecasRes, categoriasRes, fornecedoresRes] = await Promise.all([
        api.get('/pecas'),
        api.get('/categorias'),
        api.get('/fornecedores')
      ]);
      setPecas(pecasRes.data);
      setCategorias(categoriasRes.data);
      setFornecedores(fornecedoresRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    }
  };

  // Calcular preço de venda baseado no custo e margem
  const calcularPrecoVenda = (custo, margemPercent) => {
    const custoNum = parseFloat(custo) || 0;
    const margemNum = parseFloat(margemPercent) || 0;
    if (custoNum > 0 && margemNum > 0) {
      return custoNum * (1 + margemNum / 100);
    }
    return custoNum;
  };

  // Calcular margem baseada no custo e preço de venda
  const calcularMargem = (custo, precoVenda) => {
    const custoNum = parseFloat(custo) || 0;
    const vendaNum = parseFloat(precoVenda) || 0;
    if (custoNum > 0 && vendaNum > custoNum) {
      return ((vendaNum - custoNum) / custoNum) * 100;
    }
    return 0;
  };

  const handleCustoChange = (valor) => {
    const novoPrecoVenda = calcularPrecoVenda(valor, margem);
    setFormData({ 
      ...formData, 
      preco_custo: valor,
      preco_venda: novoPrecoVenda > 0 ? Math.round(novoPrecoVenda * 100) / 100 : ''
    });
  };

  const handleMargemChange = (valor) => {
    setMargem(valor);
    const novoPrecoVenda = calcularPrecoVenda(formData.preco_custo, valor);
    setFormData({ 
      ...formData, 
      preco_venda: novoPrecoVenda > 0 ? Math.round(novoPrecoVenda * 100) / 100 : ''
    });
  };

  const handlePrecoVendaChange = (valor) => {
    const novaMargem = calcularMargem(formData.preco_custo, valor);
    setMargem(novaMargem > 0 ? Math.round(novaMargem * 100) / 100 : '');
    setFormData({ ...formData, preco_venda: valor });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Converter valores vazios para números antes de enviar
      const dataToSend = {
        ...formData,
        quantidade: parseInt(formData.quantidade) || 0,
        estoque_minimo: parseInt(formData.estoque_minimo) || 0,
        preco_custo: parseFloat(formData.preco_custo) || 0,
        preco_venda: parseFloat(formData.preco_venda) || 0
      };
      
      if (editingPeca) {
        await api.put(`/pecas/${editingPeca.id}`, dataToSend);
        toast.success('Item atualizado com sucesso');
      } else {
        await api.post('/pecas', dataToSend);
        toast.success('Item cadastrado com sucesso');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar item');
      console.error(error);
    }
  };

  const handleEdit = (peca) => {
    setEditingPeca(peca);
    const custoAtual = peca.preco_custo || 0;
    const vendaAtual = peca.preco_venda || 0;
    const margemAtual = calcularMargem(custoAtual, vendaAtual);
    setMargem(Math.round(margemAtual * 100) / 100);
    setFormData({
      tipo: peca.tipo || 'peca',
      nome: peca.nome,
      codigo: peca.codigo,
      categoria_id: peca.categoria_id || '',
      fornecedor_id: peca.fornecedor_id || '',
      quantidade: peca.quantidade || 0,
      estoque_minimo: peca.estoque_minimo || 0,
      preco_custo: custoAtual,
      preco_venda: vendaAtual
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este item?')) {
      try {
        await api.delete(`/pecas/${id}`);
        toast.success('Item deletado com sucesso');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar item');
        console.error(error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'peca',
      nome: '',
      codigo: '',
      categoria_id: '',
      fornecedor_id: '',
      quantidade: '',
      estoque_minimo: '',
      preco_custo: '',
      preco_venda: ''
    });
    setMargem('');
    setEditingPeca(null);
  };

  const filteredPecas = pecas.filter(peca =>
    peca.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    peca.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pecasEstoqueBaixo = pecas.filter(p => p.tipo !== 'servico' && p.quantidade <= p.estoque_minimo);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="estoque-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Estoque</h1>
          <p className="text-sm text-slate-500">Gerencie suas peças e serviços</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm w-full sm:w-auto" data-testid="add-peca-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Item
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto mx-2">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold uppercase">
                {editingPeca ? 'Editar Item' : 'Novo Item'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo de Item */}
              <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
                <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Tipo de Item</Label>
                <div className="flex gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo: 'peca' })}
                    className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-sm border transition-all text-sm sm:text-base ${
                      formData.tipo === 'peca'
                        ? 'bg-electric-blue border-electric-blue text-white'
                        : 'bg-carbon border-white/10 text-slate-400 hover:border-white/20'
                    }`}
                    data-testid="tipo-peca-btn"
                  >
                    <Package className="w-4 h-4 inline mr-1" /> Peça
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo: 'servico' })}
                    className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-sm border transition-all text-sm sm:text-base ${
                      formData.tipo === 'servico'
                        ? 'bg-electric-blue border-electric-blue text-white'
                        : 'bg-carbon border-white/10 text-slate-400 hover:border-white/20'
                    }`}
                    data-testid="tipo-servico-btn"
                  >
                    🔧 Serviço
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Nome</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Código</Label>
                  <Input
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                  />
                </div>
              </div>

              {formData.tipo === 'peca' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Categoria</Label>
                      <Select value={formData.categoria_id} onValueChange={(value) => setFormData({ ...formData, categoria_id: value })}>
                        <SelectTrigger className="bg-obsidian border-white/10 text-white">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-carbon border-white/10 text-white">
                          {categorias.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Fornecedor</Label>
                      <Select value={formData.fornecedor_id} onValueChange={(value) => setFormData({ ...formData, fornecedor_id: value })}>
                        <SelectTrigger className="bg-obsidian border-white/10 text-white">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-carbon border-white/10 text-white">
                          {fornecedores.map(forn => (
                            <SelectItem key={forn.id} value={forn.id}>{forn.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Quantidade</Label>
                      <Input
                        type="number"
                        value={formData.quantidade}
                        onChange={(e) => setFormData({ ...formData, quantidade: e.target.value })}
                        className="bg-obsidian border-white/10 text-white"
                        placeholder=""
                        min="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-400">Estoque Mínimo</Label>
                      <Input
                        type="number"
                        value={formData.estoque_minimo}
                        onChange={(e) => setFormData({ ...formData, estoque_minimo: e.target.value })}
                        className="bg-obsidian border-white/10 text-white"
                        placeholder=""
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Seção de Preços com Margem */}
                  <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
                    <Label className="text-xs uppercase tracking-wider text-electric-blue mb-3 block flex items-center gap-2">
                      <Percent className="w-4 h-4" /> Preços e Margem de Lucro
                    </Label>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Preço de Custo</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.preco_custo}
                          onChange={(e) => handleCustoChange(e.target.value)}
                          className="bg-carbon border-white/10 text-white"
                          placeholder=""
                          min="0"
                          data-testid="preco-custo-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Margem (%)</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            step="0.1"
                            value={margem}
                            onChange={(e) => handleMargemChange(e.target.value)}
                            className="bg-carbon border-white/10 text-white pr-8"
                            placeholder=""
                            min="0"
                            data-testid="margem-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Preço de Venda</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.preco_venda}
                          onChange={(e) => handlePrecoVendaChange(e.target.value)}
                          className="bg-carbon border-white/10 text-white font-semibold"
                          placeholder=""
                          min="0"
                          data-testid="preco-venda-input"
                        />
                      </div>
                    </div>

                    {/* Preview do lucro */}
                    {parseFloat(formData.preco_custo) > 0 && parseFloat(formData.preco_venda) > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="text-slate-400">
                          Lucro por unidade: 
                          <span className="text-green-500 font-semibold ml-2">
                            {formatCurrency(parseFloat(formData.preco_venda) - parseFloat(formData.preco_custo))}
                          </span>
                        </span>
                        <span className="text-slate-500 text-xs">
                          ({parseFloat(margem || 0).toFixed(1)}% de margem)
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {formData.tipo === 'servico' && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Valor do Serviço</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.preco_venda}
                    onChange={(e) => setFormData({ ...formData, preco_venda: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                    placeholder="Ex: 150.00"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-peca-btn">
                  {editingPeca ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alertas de Estoque Baixo */}
      {pecasEstoqueBaixo.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-safety-orange/10 border border-safety-orange/30 rounded-md p-3 sm:p-4 mb-4 sm:mb-6"
        >
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-safety-orange flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-safety-orange mb-1 text-sm sm:text-base">Alerta de Estoque Baixo</h3>
              <p className="text-xs sm:text-sm text-slate-300">
                {pecasEstoqueBaixo.length} peça(s) com estoque abaixo do mínimo
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Search */}
      <div className="mb-4 sm:mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar por nome ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-carbon border-white/10 text-white"
            data-testid="search-pecas-input"
          />
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="hidden sm:block bg-carbon border border-white/5 rounded-md overflow-hidden shadow-lg">
        <table className="w-full">
          <thead className="bg-white/2 border-b border-white/5">
            <tr>
              <th className="text-left px-4 lg:px-6 py-3 lg:py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Item</th>
              <th className="text-left px-4 lg:px-6 py-3 lg:py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Código</th>
              <th className="text-left px-4 lg:px-6 py-3 lg:py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Qtd</th>
              <th className="text-left px-4 lg:px-6 py-3 lg:py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Preço</th>
              <th className="text-right px-4 lg:px-6 py-3 lg:py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredPecas.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhum item cadastrado</p>
                </td>
              </tr>
            ) : (
              filteredPecas.map((peca) => (
                <tr key={peca.id} className="table-row border-b border-white/5" data-testid={`peca-row-${peca.id}`}>
                  <td className="px-4 lg:px-6 py-3 lg:py-4 text-sm text-slate-300">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${peca.tipo === 'servico' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {peca.tipo === 'servico' ? 'SVC' : 'PÇA'}
                      </span>
                      {peca.nome}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-3 lg:py-4 text-sm text-slate-300 font-mono">{peca.codigo}</td>
                  <td className="px-4 lg:px-6 py-3 lg:py-4 text-sm">
                    {peca.tipo === 'servico' ? (
                      <span className="text-slate-500">-</span>
                    ) : (
                      <span className={`${peca.quantidade <= peca.estoque_minimo ? 'text-safety-orange font-semibold' : 'text-slate-300'}`}>
                        {peca.quantidade}
                        {peca.quantidade <= peca.estoque_minimo && (
                          <AlertTriangle className="inline w-4 h-4 ml-1" />
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-3 lg:py-4 text-sm text-slate-300">
                    {formatCurrency(peca.preco_venda)}
                  </td>
                  <td className="px-4 lg:px-6 py-3 lg:py-4 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(peca)}
                        className="text-electric-blue hover:text-white hover:bg-white/5 p-2"
                        data-testid={`edit-peca-${peca.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(peca.id)}
                        className="text-alert-red hover:text-white hover:bg-white/5 p-2"
                        data-testid={`delete-peca-${peca.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="sm:hidden space-y-3">
        {filteredPecas.length === 0 ? (
          <div className="bg-carbon border border-white/5 rounded-md p-8 text-center">
            <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nenhum item cadastrado</p>
          </div>
        ) : (
          filteredPecas.map((peca) => (
            <div key={peca.id} className="bg-carbon border border-white/5 rounded-md p-4" data-testid={`peca-card-${peca.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${peca.tipo === 'servico' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {peca.tipo === 'servico' ? 'SERVIÇO' : 'PEÇA'}
                    </span>
                    {peca.tipo !== 'servico' && peca.quantidade <= peca.estoque_minimo && (
                      <AlertTriangle className="w-4 h-4 text-safety-orange" />
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-white truncate">{peca.nome}</h3>
                  <p className="text-xs text-slate-500 font-mono">{peca.codigo}</p>
                </div>
                <span className="text-base font-bold text-white">{formatCurrency(peca.preco_venda)}</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                {peca.tipo !== 'servico' ? (
                  <span className={`text-xs ${peca.quantidade <= peca.estoque_minimo ? 'text-safety-orange' : 'text-slate-400'}`}>
                    Estoque: {peca.quantidade}
                  </span>
                ) : (
                  <span className="text-xs text-slate-500">Serviço</span>
                )}
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(peca)}
                    className="text-electric-blue hover:text-white hover:bg-white/5 p-2 h-8"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(peca.id)}
                    className="text-alert-red hover:text-white hover:bg-white/5 p-2 h-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Estoque;
